import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { Select } from 'shared/ui/Select/Select'
import { EnumDropdownItemVariant, Button, type IDropdownItem } from 'shared/ui'
import type { IFilterValue } from 'entities/Filters/types'
import type { IWidgetProps } from 'widgets/FilterEditor/types/types'
import { WidgetMultiRecurringTypeCard } from './WidgetMultiRecurringTypeCard'
import { WidgetMultiRecurringTypeTrigger } from './WidgetMultiRecurringTypeTrigger'
import styles from './styles.module.scss'

export const WidgetMultiRecurringType = observer(
  ({
    filterItem,
    expandByDefault = false,
    parentsDropdownId,
  }: IWidgetProps<IFilterValue, string[]>) => {
    const [showDropdown, setShowDropdown] = useState(false)
    const [showValuesWarning, setShowValuesWarning] = useState(false)
    const filterConfig = filterItem.config
    const payloadValue = useMemo(
      () => (Array.isArray(filterItem.payload.value) ? filterItem.payload.value : []),
      [filterItem.payload.value]
    )
    const values = filterConfig.values ?? []
    const [primaryKeys, setPrimaryKeys] = useState<string[]>([])
    const [selectedKeys, setSelectedKeys] = useState(payloadValue)
    const dropdownItems: IDropdownItem[] = useMemo(
      () =>
        values
          .reduce<[IFilterValue[], IFilterValue[]]>(
            (values, value) => {
              const isSelected = primaryKeys.includes(String(value.key))

              if (isSelected) values[0].push(value)
              else values[1].push(value)

              return values
            },
            [[], []]
          )
          .flat()
          .map((item, index) => {
            const key = String(item.key)

            return {
              id: key,
              label: item.label,
              labelContent: <WidgetMultiRecurringTypeCard label={item.label} />,
              variant: EnumDropdownItemVariant.Checkbox,
              activeValue: selectedKeys.includes(key),
              divider: index === primaryKeys.length - 1 && primaryKeys.length !== values.length,
            }
          }),
      [selectedKeys, primaryKeys]
    )

    useEffect(() => {
      setPrimaryKeys(payloadValue)
    }, [])

    useEffect(() => {
      setTimeout(() => setShowDropdown(expandByDefault), 100)
    }, [expandByDefault])

    return (
      <Select
        text={filterItem.operator?.key ?? ''}
        type='multi'
        items={dropdownItems}
        dropdownProps={{
          ariaLabel: 'Select',
          parentsDropdownId: parentsDropdownId,
          show: showDropdown,
          textFieldProps: {
            InputProps: { placeholder: `Search ${filterItem.config.name}` },
          },
          bottomActions: selectedKeys.length
            ? () => (
                <Button
                  text='Clear'
                  contained='secondary'
                  typeBtn='text'
                  onClick={() => {
                    setPrimaryKeys([])
                    setSelectedKeys([])
                    filterItem.payload.updateHard({ value: [] })
                  }}
                />
              )
            : undefined,
          className: styles.operatorDropdown,
          withSearch: true,
          onClose: () => {
            setPrimaryKeys(selectedKeys)
            setShowValuesWarning(!selectedKeys.length)
          },
          triggerComponent: () => (
            <div
              className={classNames(styles.selectTriggerComponent, {
                [styles.warning]: showValuesWarning,
              })}
            >
              <WidgetMultiRecurringTypeTrigger selectedKeys={selectedKeys} items={values} />
            </div>
          ),
          onChange: (item) => {
            const id = String(item.id)
            const keys = item.activeValue
              ? selectedKeys.filter((key) => key !== id)
              : [...selectedKeys, id]

            setShowValuesWarning(false)
            setSelectedKeys(keys)
            filterItem.payload.updateHard({ value: keys })
          },
        }}
      />
    )
  }
)
